import { faCircleQuestion, faPlus, faSave, faTimes, faTrash } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  ActionIcon,
  Alert,
  Badge,
  Button,
  Card,
  Center,
  Divider,
  Grid,
  Group,
  Loader,
  Modal,
  NumberInput,
  Select,
  Stack,
  Text,
  TextInput,
  Title,
  Tooltip,
} from '@mantine/core';
import { DatePickerInput } from '@mantine/dates';
import { DateTime } from 'luxon';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';

import {
  attachPayrateToAssociation,
  detachPayrateFromAssociation,
  getAssociationPayrates,
} from '../../data/actions/ratesActions';
import { getManageableAssociations } from '../../data/actions/associationActions';
import { ICreateAssociationPayrateAttachmentRequest } from '../../data/api/ratesApi';
import { IState } from '../../data/reducers/state';
import { IAssociation } from '../../types/association/Association';
import { IPayRate, IPayrateAttachment } from '../../types/payrates/PayRate';
import { ITag, TagType } from '../../types/tags/tag';
import { toDateTimeNullable } from '../../util/date+helper';
import { confirm as openConfirmModal } from '../common/Confirm';
import AssociationsApi from '../../data/api/associationsApi';

interface AssociationPayrateAttacherProps {
  payrates: { [id: string]: IPayRate };
  associations: IAssociation[];
  selectedAssociation?: IAssociation;
  selectedAssociationId?: string;
  onAssociationChange?: (associationId: string) => void;
  effectiveDate?: Date | string | DateTime;
  onEffectiveDateChange?: (date: Date) => void;
  isAdmin?: boolean;
  variant?: "full" | "button";
  buttonLabel?: string;
  actions: any;
}

interface AttachmentFormData {
  payrateId: string;
  associationId: string;
  start?: Date;
  finish?: Date;
  priority: number;
  tags: ITag[];
}

const AssociationPayrateAttacher: React.FC<AssociationPayrateAttacherProps> = ({
  payrates,
  associations,
  selectedAssociation,
  selectedAssociationId,
  onAssociationChange,
  effectiveDate,
  onEffectiveDateChange,
  actions,
  isAdmin = false,
  variant = "full",
  buttonLabel,
}) => {
  const [loading, setLoading] = useState(true);
  const [showAttachModal, setShowAttachModal] = useState(false);
  const effectiveDateKey = useMemo(() => {
    const dt = toDateTimeNullable(effectiveDate as any);
    return dt ? dt.toISODate() : 'today';
  }, [effectiveDate]);
  const lastRefreshKeyRef = useRef<string | null>(null);
  const createDefaultDateRange = () => {
    // if in December, make it start to end of next year
    const isEndOfYear = new Date().getMonth() === 11
    const yearToCreate = new Date().getFullYear() + (isEndOfYear ? 1 : 0);
    const start = isEndOfYear ? new Date(yearToCreate, 0, 1) : new Date();

    const finish = new Date(yearToCreate, 11, 31, 23, 59, 59, 999);
    return { start, finish };
  };

  const [formData, setFormData] = useState<AttachmentFormData>(() => {
    const { start, finish } = createDefaultDateRange();
    return {
      payrateId: '',
      associationId: selectedAssociation?._id || '',
      start,
      finish,
      priority: 100,
      tags: [],
    };
  });
  const [attachments, setAttachments] = useState<IPayrateAttachment[]>([]);

  useEffect(() => {
    loadData();
  }, [selectedAssociation?._id, effectiveDateKey]);

  useEffect(() => {
    if (selectedAssociation) {
      setFormData(prev => ({
        ...prev,
        associationId: selectedAssociation._id,
      }));
    }
  }, [selectedAssociation]);

  const loadData = async () => {
    const refreshKey = `${selectedAssociation?._id || 'none'}|${effectiveDateKey}`;
    if (lastRefreshKeyRef.current === refreshKey) return;
    setLoading(true);
    try {
      await actions.getAssociationPayrates();

      if (selectedAssociation?._id) {
        const effectiveDt = toDateTimeNullable(effectiveDate as any) || DateTime.local().startOf('day');
        const effectiveISO = effectiveDt?.toISO();
        const associations = isAdmin
          ? await AssociationsApi.getAdminAssociations(effectiveISO)
          : await AssociationsApi.getMyAssociations(effectiveISO);
        const refreshedAssociation = associations.find((a) => a._id === selectedAssociation._id);
        if (refreshedAssociation?.payrates) {
          setAttachments([...refreshedAssociation.payrates]);
        } else {
          setAttachments([]);
        }
      } else if (selectedAssociation?.payrates) {
        setAttachments([...selectedAssociation.payrates]);
      } else {
        setAttachments([]);
      }
      lastRefreshKeyRef.current = refreshKey;
    } catch (error: any) {
      console.error('Failed to load data:', error.message || error);
      setAttachments([]);
    } finally {
      setLoading(false);
    }
  };

  const handleAttach = () => {
    if (!isAdmin) return;
    if (!selectedAssociation?._id) {
      console.error('A selected association is required to attach a payrate.');
      return;
    }
    const { start, finish } = createDefaultDateRange();
    setFormData({
      payrateId: '',
      associationId: selectedAssociation?._id || '',
      start,
      finish,
      priority: 100,
      tags: [],
    });
    setShowAttachModal(true);
  };

  const handleSaveAttachment = async () => {
    if (!isAdmin) return;
    if (!formData.payrateId || !formData.associationId) {
      console.error('Please select a payrate and association');
      return;
    }

    try {
      const attachmentData: ICreateAssociationPayrateAttachmentRequest = {
        payrateId: formData.payrateId,
        associationId: formData.associationId,
        start: formData.start,
        finish: formData.finish,
        priority: formData.priority,
        tags: formData.tags,
      };

      await actions.attachPayrateToAssociation(attachmentData);

      console.log('Payrate attached successfully');

      setShowAttachModal(false);
      const startDateTime = toDateTimeNullable(attachmentData.start);
      const finishDateTime = toDateTimeNullable(attachmentData.finish);
      const effectiveDateTime = toDateTimeNullable(effectiveDate as any);
      const isEffectiveInRange =
        !!effectiveDateTime &&
        (!startDateTime || effectiveDateTime >= startDateTime.startOf('day')) &&
        (!finishDateTime || effectiveDateTime <= finishDateTime.endOf('day'));

      if (effectiveDateTime && startDateTime && !isEffectiveInRange) {
        const startLabel = startDateTime.toLocaleString(DateTime.DATE_MED);
        const effectiveLabel = effectiveDateTime.toLocaleString(DateTime.DATE_MED);
        const jump = await openConfirmModal({
          title: 'Payrate starts in the future',
          message: `Added payrate starts ${startLabel}, but you're viewing payrates effective for ${effectiveLabel}. Jump to ${startLabel}?`,
          confirmText: `Jump to ${startLabel}`,
          cancelText: 'No',
        });
        if (jump && onEffectiveDateChange) {
          onEffectiveDateChange(startDateTime.startOf('day').toJSDate());
        }
      }

      // Refresh association state in the store so the payrates viewer reflects the new attachment
      const effectiveDtForRefresh = toDateTimeNullable(effectiveDate as any) || DateTime.local().startOf('day');
      const effectiveISOForRefresh = effectiveDtForRefresh?.toISO();
      await actions.getManageableAssociations(effectiveISOForRefresh);

      loadData(); // Refresh the list
    } catch (error: any) {
      console.error('Failed to attach payrate:', error.message || error);
    }
  };

  const handleDetach = async (attachment: IPayrateAttachment) => {
    if (!isAdmin) return;
    if (window.confirm('Are you sure you want to remove this payrate attachment?')) {
      try {
        await actions.detachPayrateFromAssociation(attachment._id);
        console.log('Payrate detached successfully');
        loadData(); // Refresh the list
      } catch (error: any) {
        console.error('Failed to detach payrate:', error.message || error);
      }
    }
  };

  const getPayrateName = (payrateId: string) => {
    const payrate = payrates[payrateId];
    return payrate?.name || 'Unknown Payrate';
  };

  const toJsDate = (value?: any): Date | undefined => {
    if (!value) return undefined;
    if (value instanceof Date) return value;
    if (typeof value === 'string' || typeof value === 'number') {
      const parsed = new Date(value);
      return Number.isNaN(parsed.getTime()) ? undefined : parsed;
    }
    if (value && typeof value.toJSDate === 'function') {
      return value.toJSDate();
    }
    try {
      const parsed = new Date(value);
      return Number.isNaN(parsed.getTime()) ? undefined : parsed;
    } catch {
      return undefined;
    }
  };

  const formatDateRange = (start?: unknown, finish?: unknown) => {
    const startDate = toJsDate(start);
    const finishDate = toJsDate(finish);

    if (!startDate && !finishDate) return 'Always';
    if (startDate && finishDate) {
      return `${startDate.toLocaleDateString()} - ${finishDate.toLocaleDateString()}`;
    }
    if (startDate) return `From ${startDate.toLocaleDateString()}`;
    if (finishDate) return `Until ${finishDate.toLocaleDateString()}`;
    return 'Always';
  };

  const getAttachmentStatus = (attachment: IPayrateAttachment) => {
    const now = Date.now();
    const startDate = toJsDate(attachment.start);
    const finishDate = toJsDate(attachment.finish);
    const startsInFuture = startDate ? startDate.getTime() > now : false;
    const finishedInPast = finishDate ? finishDate.getTime() < now : false;

    if (startsInFuture) {
      return {
        isActive: false,
        badge: 'Upcoming',
        color: 'yellow',
        description: `Starts ${startDate!.toLocaleDateString()}`,
      };
    }

    if (finishedInPast) {
      return {
        isActive: false,
        badge: 'Expired',
        color: 'gray',
        description: `Ended ${finishDate!.toLocaleDateString()}`,
      };
    }

    return {
      isActive: true,
      badge: 'Active',
      color: 'green',
      description: 'Applies to games right now',
    };
  };

  const availablePayrates = useMemo(() => {
    const grouped: Record<string, { value: string; label: string }[]> = {};

    Object.values(payrates || {}).forEach((payrate) => {
      if (!payrate?._id) return;
      const groupKey = payrate.associationId ? 'Association payrates' : 'System payrates';

      if (!grouped[groupKey]) {
        grouped[groupKey] = [];
      }

      grouped[groupKey].push({
        value: payrate._id,
        label: `${payrate.name} ${payrate.associationId ? '(Association)' : '(System)'}`,
      });
    });

    const sortByLabel = (a: { label: string }, b: { label: string }) =>
      a.label.localeCompare(b.label, undefined, { sensitivity: 'base' });

    const entries = Object.entries(grouped)
      .sort(([groupA], [groupB]) =>
        groupA.localeCompare(groupB, undefined, { sensitivity: 'base' }),
      )
      .map(([group, items]) => ({
        group,
        items: [...items].sort(sortByLabel),
      }));

    // Mantine Select accepts either grouped data or a flat array. If we only have
    // one category, keep it flat so the combobox doesn't render unnecessary headings.
    if (entries.length === 1) {
      return entries[0].items;
    }

    return entries;
  }, [payrates]);
  const associationOptions = associations.map(association => ({
    value: association._id,
    label: association.name,
  }));

  const tagTypeOptions = useMemo(() => {
    const numericValues = [
      TagType.custom,
      TagType.gameMode,
      TagType.gameDuration,
    ]

    return numericValues.map((value) => ({
      value: value.toString(),
      label: TagType.toString(value),
    }));
  }, []);

  const defaultTagType = useMemo(() => {
    return tagTypeOptions.length > 0
      ? Number(tagTypeOptions[0].value) as TagType
      : TagType.gameMode;
  }, [tagTypeOptions]);

  const addTag = () => {
    if (tagTypeOptions.length === 0) return;
    setFormData((prev) => ({
      ...prev,
      tags: [
        ...prev.tags,
        {
          type: defaultTagType,
          value: '',
        },
      ],
    }));
  };

  const updateTagType = (index: number, value: string | null) => {
    if (value === null) return;
    const parsedType = Number(value);
    if (Number.isNaN(parsedType)) return;
    const type = parsedType as TagType;
    setFormData((prev) => ({
      ...prev,
      tags: prev.tags.map((tag, i) =>
        i === index
          ? {
            ...tag,
            type,
          }
          : tag,
      ),
    }));
  };

  const updateTagValue = (index: number, value: string) => {
    setFormData((prev) => ({
      ...prev,
      tags: prev.tags.map((tag, i) =>
        i === index
          ? {
            ...tag,
            value,
          }
          : tag,
      ),
    }));
  };

  const removeTag = (index: number) => {
    setFormData((prev) => ({
      ...prev,
      tags: prev.tags.filter((_, i) => i !== index),
    }));
  };

  const sortedAttachments = useMemo(() => {
    return [...attachments].sort((a, b) => (b.priority || 100) - (a.priority || 100));
  }, [attachments]);

  const { activeAttachments, inactiveAttachments } = useMemo(() => {
    const active: IPayrateAttachment[] = [];
    const inactive: IPayrateAttachment[] = [];

    for (const attachment of sortedAttachments) {
      const status = getAttachmentStatus(attachment);
      if (status.isActive) {
        active.push(attachment);
      } else {
        inactive.push(attachment);
      }
    }

    return { activeAttachments: active, inactiveAttachments: inactive };
  }, [sortedAttachments]);

  const getPayrateCategoryBadges = (payrate?: IPayRate) => {
    if (!payrate) return [];
    const categories: string[] = [];
    if (payrate.referee) categories.push('Referee');
    if (payrate.scoretable) categories.push('Scoretable');
    if (payrate.refereeCoach) categories.push('Referee Coach');
    if (payrate.statistician) categories.push('Statistician');
    if (payrate.supervisor) categories.push('Supervisor');
    if (payrate.admin) categories.push('Admin');
    return categories;
  };

  const renderAttachmentCard = (attachment: IPayrateAttachment) => {
    const payrate = payrates[attachment.payrateId];
    const categories = getPayrateCategoryBadges(payrate);
    const tags: ITag[] = attachment.tags ? [...attachment.tags] : [];
    const hasTags = tags.length > 0;
    const originLabel = payrate?.associationId
      ? 'Association payrate'
      : 'System payrate';
    const status = getAttachmentStatus(attachment);

    return (
      <Card key={attachment._id} withBorder shadow="sm" radius="md">
        <Stack gap="sm">
          <Group justify="space-between" align="flex-start">
            <Stack gap={4}>
              <Group gap="xs" wrap="wrap">
                <Text fw={600} size="lg">
                  {getPayrateName(attachment.payrateId)}
                </Text>
                {attachment.inherited && (
                  <Badge color="gray" variant="light" size="sm">
                    Inherited
                  </Badge>
                )}
              </Group>
              <Group gap="xs" wrap="wrap">
                <Badge
                  color={payrate?.associationId ? 'blue' : 'gray'}
                  variant="light"
                  size="sm"
                >
                  {originLabel}
                </Badge>
                {categories.map((category) => (
                  <Badge
                    key={category}
                    color="teal"
                    variant="light"
                    size="sm"
                  >
                    {category}
                  </Badge>
                ))}
              </Group>
            </Stack>
            <Group gap="sm" align="flex-start">
              <Stack gap={2} align="center">
                <Text size="xs" c="dimmed">
                  Status
                </Text>
                <Badge color={status.color} variant="filled">
                  {status.badge}
                </Badge>
                <Text size="xs" c="dimmed">
                  {status.description}
                </Text>
              </Stack>
              <Stack gap={2} align="center">
                <Text size="xs" c="dimmed">
                  Priority
                </Text>
                <Badge color="blue" size="lg" variant="filled">
                  {attachment.priority || 100}
                </Badge>
              </Stack>
              {isAdmin && (
                <Tooltip label="Remove Attachment">
                  <ActionIcon
                    variant="subtle"
                    color="red"
                    onClick={() => handleDetach(attachment)}
                  >
                    <FontAwesomeIcon icon={faTrash} size="sm" />
                  </ActionIcon>
                </Tooltip>
              )}
            </Group>
          </Group>

          <Divider />

          <Grid>
            <Grid.Col span={{ base: 12, md: 4 }}>
              <Stack gap={2}>
                <Text size="xs" c="dimmed">
                  Date range
                </Text>
                <Text>
                  {formatDateRange(attachment.start, attachment.finish)}
                </Text>
              </Stack>
            </Grid.Col>
            <Grid.Col span={{ base: 12, md: 5 }}>
              <Stack gap={2}>
                <Text size="xs" c="dimmed">
                  Applies to
                </Text>
                {hasTags ? (
                  <Group gap="xs" wrap="wrap">
                    {tags.map((tag: ITag) => (
                      <Badge
                        key={`${tag.type}-${tag.value}`}
                        color="violet"
                        variant="light"
                        size="sm"
                      >
                        {ITag.toString(tag)}
                      </Badge>
                    ))}
                  </Group>
                ) : (
                  <Text>All games</Text>
                )}
              </Stack>
            </Grid.Col>
            <Grid.Col span={{ base: 12, md: 3 }}>
              <Stack gap={2}>
                <Text size="xs" c="dimmed">
                  Payrate ID
                </Text>
                <Text>{payrate?._id || '—'}</Text>
              </Stack>
            </Grid.Col>
          </Grid>
        </Stack>
      </Card>
    );
  };

  const attachButtonLabelText = buttonLabel ?? 'Attach Payrate';
  const canAttach = Boolean(selectedAssociation?._id);

  const attachButtonElement = (
    <Button
      onClick={handleAttach}
      disabled={!canAttach}
    >
      <FontAwesomeIcon icon={faPlus} className="me-2" />
      {attachButtonLabelText}
    </Button>
  );

  const attachButton = isAdmin
    ? (canAttach ? attachButtonElement : (
      <Tooltip label="Select an association in the admin header to attach a payrate" withArrow>
        <span style={{ display: 'inline-block' }}>{attachButtonElement}</span>
      </Tooltip>
    ))
    : null;

  const attachModal = !isAdmin ? null : (
    <Modal
      opened={showAttachModal}
      onClose={() => setShowAttachModal(false)}
      title="Attach Payrate to Association"
      size="lg"
    >
      <Stack gap="md">
        <Select
          label="Payrate"
          placeholder="Select a payrate to attach"
          value={formData.payrateId}
          onChange={(value) => setFormData(prev => ({ ...prev, payrateId: value || '' }))}
          data={availablePayrates}
          searchable
          required
        />

        <Grid>
          <Grid.Col span={6}>
            <DatePickerInput
              label="Start Date"
              placeholder="Select start date (optional)"
              value={formData.start}
              onChange={(value) => {
                setFormData(prev => ({ ...prev, start: toJsDate(value) }));
              }}
              clearable
            />
          </Grid.Col>
          <Grid.Col span={6}>
            <DatePickerInput
              label="End Date"
              placeholder="Select end date (optional)"
              value={formData.finish}
              onChange={(value) => {
                setFormData(prev => ({ ...prev, finish: toJsDate(value) }));
              }}
              clearable
            />
          </Grid.Col>
        </Grid>

        <NumberInput
          label="Priority"
          description="Higher numbers have higher priority. Default: 100"
          value={formData.priority}
          onChange={(value) => {
            const numeric = typeof value === 'number' && !Number.isNaN(value) ? value : 100;
            setFormData(prev => ({ ...prev, priority: numeric }));
          }}
          min={1}
          max={999}
          step={1}
        />

        <Stack gap="xs">
          <Group justify="space-between" align="center">
            <Text size="sm" fw={500}>
              Tags / rule filters
            </Text>
            <Group gap="xs">
              <Tooltip
                label={(
                  <Stack gap={2}>
                    <Text size="xs">custom: Tag added to a competition.</Text>
                    <Text size="xs">gameMode: The specific mode set on a game.</Text>
                    <Text size="xs">gameDuration: Game length in minutes.</Text>
                  </Stack>
                )}
                multiline
                maw={280}
              >
                <ActionIcon variant="subtle" size="sm" aria-label="Tag help">
                  <FontAwesomeIcon icon={faCircleQuestion} size="sm" />
                </ActionIcon>
              </Tooltip>
              <Button
                variant="subtle"
                size="xs"
                leftSection={<FontAwesomeIcon icon={faPlus} size="sm" />}
                onClick={addTag}
              >
                Add tag
              </Button>
            </Group>
          </Group>

          {formData.tags.length === 0 ? (
            <Alert color="gray" title="No tags">
              Without tags this rule applies to all games that fall within the date range.
            </Alert>
          ) : (
            <Stack gap="xs">
              {formData.tags.map((tag, index) => (
                <Group key={index} align="flex-end">
                  <Select
                    label="Type"
                    placeholder="Select tag type"
                    value={String(tag.type)}
                    onChange={(value) => updateTagType(index, value)}
                    data={tagTypeOptions}
                    style={{ width: 200 }}
                  />
                  <TextInput
                    label="Value"
                    placeholder="Enter tag value"
                    value={tag.value}
                    onChange={(event) => updateTagValue(index, event.currentTarget.value)}
                    style={{ flexGrow: 1 }}
                  />
                  <ActionIcon
                    color="red"
                    variant="subtle"
                    onClick={() => removeTag(index)}
                    aria-label="Remove tag"
                  >
                    <FontAwesomeIcon icon={faTrash} size="sm" />
                  </ActionIcon>
                </Group>
              ))}
            </Stack>
          )}
        </Stack>

        <Group justify="right">
          <Button
            variant="outline"
            onClick={() => setShowAttachModal(false)}
          >
            <FontAwesomeIcon icon={faTimes} className="me-2" />
            Cancel
          </Button>
          <Button
            onClick={handleSaveAttachment}
          >
            <FontAwesomeIcon icon={faSave} className="me-2" />
            Attach Payrate
          </Button>
        </Group>
      </Stack>
    </Modal>
  );

  if (variant === "button") {
    return (
      <>
        {attachButton}
        {attachModal}
      </>
    );
  }

  if (!selectedAssociation) {
    return (
      <>
        <Alert title="No Association Selected">
          Please select an association to manage payrate attachments.
        </Alert>
        {attachModal}
      </>
    );
  }

  if (loading) {
    return (
      <Center h={200}>
        <Loader size="lg" />
      </Center>
    );
  }

  return (
    <>
      <Card padding="lg" shadow="sm">
        <Stack gap="md">
          <Group justify="space-between" align="flex-end">
            <Title order={3}>
              Payrate Rules for {selectedAssociation?.name}
            </Title>
            <Group gap="sm">
              {onAssociationChange && (
                <Select
                  label="Association"
                  placeholder="Select an association"
                  value={selectedAssociationId || selectedAssociation._id}
                  onChange={(value) => onAssociationChange(value || '')}
                  data={associationOptions}
                  style={{ minWidth: 220 }}
                  searchable
                />
              )}
              {attachButton}
            </Group>
          </Group>

          <Text size="sm" color="dimmed">
            Payrate rules determine which payrates apply to games based on date ranges,
            game modes, and priorities. Higher priority rules override lower priority ones.
          </Text>

          {sortedAttachments.length === 0 ? (
            <Alert title="No Payrate Rules">
              No payrate rules are currently configured for this association.
              Add rules to automatically apply payrates to games.
            </Alert>
          ) : (
            <Stack gap="lg">
              <Stack gap="sm">
                <Text fw={600} size="md">
                  Active rules
                </Text>
                {activeAttachments.length > 0 ? (
                  <Stack gap="md">
                    {activeAttachments.map(renderAttachmentCard)}
                  </Stack>
                ) : (
                  <Alert color="green" title="No active rules">
                    No payrate rules currently apply. Create or edit rules to
                    cover the present date range.
                  </Alert>
                )}
              </Stack>

              {inactiveAttachments.length > 0 && (
                <>
                  <Divider label="Inactive rules" />
                  <Stack gap="sm">
                    <Text fw={600} size="md">
                      Inactive rules
                    </Text>
                    <Stack gap="md">
                      {inactiveAttachments.map(renderAttachmentCard)}
                    </Stack>
                  </Stack>
                </>
              )}
            </Stack>
          )}

          <Alert color="blue" title="Payrate Priority">
            <Stack gap="xs">
              <Text size="sm">Payrates are applied in order of priority (lower numbers = higher priority):</Text>
              <Text size="sm">• Game-specific payrates: Priority -1 (highest)</Text>
              <Text size="sm">• Shift payrates: Custom priority</Text>
              <Text size="sm">• Competition payrates: Custom priority</Text>
              <Text size="sm">• Association payrates: Priority 100+ (lowest)</Text>
            </Stack>
          </Alert>
        </Stack>
      </Card>

      {attachModal}
    </>
  );
};

const mapStateToProps = (state: IState) => ({
  payrates: state.payrates.map || {},
  associations: state.associations.admin.map(
    (a) => state.associations.map[a],
  ).filter(Boolean),
  isAdmin: true,
});

const mapDispatchToProps = (dispatch: any) => ({
  actions: bindActionCreators(
    {
      getAssociationPayrates,
      attachPayrateToAssociation,
      detachPayrateFromAssociation,
      getManageableAssociations,
    },
    dispatch
  ),
});

export default connect(mapStateToProps, mapDispatchToProps)(AssociationPayrateAttacher);
